import { useCallback, useMemo } from 'react';
import { useAuthenticationProvider } from 'infrastructure/security/authentication/AuthenticationProvider';
import { openExternalLink } from '../../shared/helpers/linkHelpers';
import { InterruptionEmployeeResponseStatus } from '../../types';
import { useInterruptionsContext } from '../../InterruptionsContext';
import useInterruptionService from '../../useInterruptionService';
import { toInterruptionEmergencyCardView } from '../../shared/helpers/emergencyInterruptionMappers';

const useInterruptionsEmergencyController = () => {
  const {
    emergencyError,
    emergencyInterruptions,
    emergencyLoading,
    removeEmergencyInterruption,
    sendInterruptionTeamsResponseMessage,
  } = useInterruptionsContext();
  const { getUserAlias } = useAuthenticationProvider();
  const { cancelInterruption, updateInterruptionStatus } = useInterruptionService();
  const currentUserId = getUserAlias() || '';

  const cards = useMemo(
    () => emergencyInterruptions.map(item => toInterruptionEmergencyCardView(item, currentUserId)),
    [currentUserId, emergencyInterruptions],
  );

  const updateEmergencyStatus = useCallback(
    async (id: string, status: InterruptionEmployeeResponseStatus) => {
      try {
        await updateInterruptionStatus(id, status);
        await sendInterruptionTeamsResponseMessage(id, status);
        removeEmergencyInterruption(id);
        return true;
      } catch (error) {
        return false;
      }
    },
    [removeEmergencyInterruption, updateInterruptionStatus, sendInterruptionTeamsResponseMessage],
  );

  const acceptEmergencyInterruption = useCallback(
    async (id: string, chatLink: string) => {
      const isSuccess = await updateEmergencyStatus(id, InterruptionEmployeeResponseStatus.Accepted);

      if (isSuccess) {
        openExternalLink(chatLink);
      }

      return isSuccess;
    },
    [updateEmergencyStatus],
  );

  return {
    cards,
    error: emergencyError,
    loading: emergencyLoading,
    onAccept: acceptEmergencyInterruption,
    onCancel: async (id: string) => {
      try {
        await cancelInterruption(id);
        removeEmergencyInterruption(id);
        return true;
      } catch (error) {
        return false;
      }
    },
    onDismiss: (id: string) => updateEmergencyStatus(id, InterruptionEmployeeResponseStatus.Dismissed),
    onExpire: removeEmergencyInterruption,
    onPostpone: (id: string) => updateEmergencyStatus(id, InterruptionEmployeeResponseStatus.Postponed),
  };
};

export default useInterruptionsEmergencyController;
